import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { showToast } from '../../features/ui/uiSlice';
import { updateTask } from '../../features/tasks/taskSlice';
import api from '../../utils/axiosConfig';
import SkillsDropdown from '../../components/UI/SkillsDropdown';
import {
    ClipboardDocumentListIcon,
    ArrowLeftIcon,
    BuildingOfficeIcon,
    CheckCircleIcon,
    CurrencyDollarIcon,
    MapPinIcon,
    UserGroupIcon,
    CalendarIcon,
    DocumentTextIcon,
    CogIcon,
    PlusIcon,
    TrashIcon,
    XMarkIcon,
    PencilIcon
} from '@heroicons/react/24/outline';

const EditTask: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { user } = useAppSelector((state: any) => state.auth);
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [companies, setCompanies] = useState<Array<{ _id: string, companyName: string }>>([]);
    const [selectedCompany, setSelectedCompany] = useState('');
    const [showBudgetScenarioModal, setShowBudgetScenarioModal] = useState(false);
    const [editingScenario, setEditingScenario] = useState<any>(null);
    const [newScenario, setNewScenario] = useState({
        name: '',
        description: '',
        range: {
            min: 1,
            max: 500
        },
        budgetDistribution: {
            teamExecutive: 0,
            teamLeader: 0,
            districtHead: 0,
            stateHead: 0,
            regionalHead: 0,
            admin: 0,
        },
        isActive: false
    });

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        taskUrl: '',
        totalBudget: '',
        budgetScenarios: [] as Array<{
            id: string;
            name: string;
            description: string;
            range: {
                min: number;
                max: number;
            };
            budgetDistribution: {
                teamExecutive: number;
                teamLeader: number;
                districtHead: number;
                stateHead: number;
                regionalHead: number;
                admin: number;
            };
            isActive: boolean;
        }>,
        currentScenario: null as string | null,
        regions: [] as string[],
        states: [] as string[],
        districts: [] as string[],
        targetDesignations: ['team_executive', 'team_leader', 'district_head', 'state_head', 'regional_head'],
        requiredSkills: [] as string[],
        startDate: '',
        endDate: '',
        validationRules: {
            requiredFields: [] as string[],
        },
    });

    const fetchTaskData = useCallback(async () => {
        try {
            const response = await api.get(`/tasks/${id}`);
            if (response.data.success) {
                const task = response.data.data;
                setFormData({
                    title: task.title || '',
                    description: task.description || '',
                    taskUrl: task.taskUrl || '',
                    totalBudget: task.totalBudget?.toString() || '',
                    budgetScenarios: task.budgetScenarios || [],
                    currentScenario: task.currentScenario || null,
                    regions: task.regions || [],
                    states: task.states || [],
                    districts: task.districts || [],
                    targetDesignations: task.targetDesignations || [],
                    requiredSkills: task.requiredSkills?.map((skill: any) => typeof skill === 'object' ? skill._id : skill) || [],
                    startDate: task.startDate ? new Date(task.startDate).toISOString().split('T')[0] : '',
                    endDate: task.endDate ? new Date(task.endDate).toISOString().split('T')[0] : '',
                    validationRules: {
                        requiredFields: task.validationRules?.requiredFields || []
                    },
                });
                if (task.company) {
                    setSelectedCompany(typeof task.company === 'object' ? task.company._id : task.company);
                }
            }
        } catch (error: any) {
            console.error('Error fetching task:', error);
            dispatch(showToast({
                message: 'Failed to fetch task data',
                type: 'error'
            }));
            navigate('/admin/tasks');
        } finally {
            setLoading(false);
        }
    }, [id, dispatch, navigate]);

    const fetchCompanies = useCallback(async () => {
        try {
            const response = await api.get('/companies');
            setCompanies(response.data.data);
        } catch (error) {
            console.error('Error fetching companies:', error);
        }
    }, []);

    useEffect(() => {
        if (id) {
            fetchTaskData();
        }
        if (user?.role === 'admin') {
            fetchCompanies();
        }
    }, [id, user, fetchTaskData, fetchCompanies]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        if (name.startsWith('validationRules.')) {
            const field = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                validationRules: {
                    ...prev.validationRules,
                    [field]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleArrayChange = (name: string, value: string[]) => {
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Geographic Scope Logic
    const regions = ['north', 'south', 'east', 'west', 'central'];
    const designations = ['team_executive', 'team_leader', 'district_head', 'state_head', 'regional_head'];

    const regionStatesMap = {
        north: ['Jammu and Kashmir', 'Himachal Pradesh', 'Punjab', 'Haryana', 'Delhi', 'Uttarakhand', 'Uttar Pradesh', 'Rajasthan'],
        south: ['Andhra Pradesh', 'Karnataka', 'Kerala', 'Tamil Nadu', 'Telangana', 'Puducherry'],
        east: ['West Bengal', 'Odisha', 'Jharkhand', 'Bihar', 'Assam', 'Arunachal Pradesh', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Sikkim', 'Tripura'],
        west: ['Maharashtra', 'Gujarat', 'Goa', 'Dadra and Nagar Haveli', 'Daman and Diu'],
        central: ['Madhya Pradesh', 'Chhattisgarh']
    };

    // State data required for district mapping
    const stateData = [
        { name: 'Andhra Pradesh', districts: ['Visakhapatnam', 'Vijayawada', 'Guntur', 'Nellore', 'Kurnool', 'Tirupati', 'Rajahmundry', 'Kadapa', 'Anantapur', 'Chittoor', 'Ongole', 'Eluru', 'Machilipatnam', 'Tenali', 'Proddatur', 'Chilakaluripet', 'Kadiri', 'Gudivada', 'Srikakulam', 'Vizianagaram'] },
        { name: 'Arunachal Pradesh', districts: ['Itanagar', 'Naharlagun', 'Pasighat', 'Tezpur', 'Bomdila', 'Tawang', 'Ziro', 'Along', 'Roing', 'Daporijo', 'Anini', 'Changlang', 'Khonsa', 'Seppa', 'Koloriang', 'Yingkiong', 'Aalo', 'Miao', 'Namsai'] },
        { name: 'Assam', districts: ['Guwahati', 'Silchar', 'Dibrugarh', 'Jorhat', 'Tezpur', 'Nagaon', 'Tinsukia', 'Barpeta', 'Dhubri', 'Kokrajhar', 'Goalpara', 'Bongaigaon', 'Mangaldoi', 'Karimganj', 'Hailakandi', 'Diphu', 'North Lakhimpur', 'Sivasagar', 'Golaghat', 'Morigaon'] },
        { name: 'Bihar', districts: ['Patna', 'Gaya', 'Bhagalpur', 'Muzaffarpur', 'Darbhanga', 'Purnia', 'Arrah', 'Begusarai', 'Katihar', 'Munger', 'Chapra', 'Bettiah', 'Motihari', 'Hajipur', 'Sitamarhi', 'Samastipur', 'Saharsa', 'Madhubani', 'Siwan', 'Buxar'] },
        { name: 'Chhattisgarh', districts: ['Raipur', 'Bhilai', 'Bilaspur', 'Korba', 'Rajnandgaon', 'Durg', 'Raigarh', 'Jagdalpur', 'Ambikapur', 'Dhamtari', 'Mahasamund', 'Kanker', 'Janjgir-Champa', 'Mungeli', 'Kabirdham', 'Sukma', 'Kondagaon', 'Balrampur', 'Surajpur', 'Balod'] },
        { name: 'Goa', districts: ['Panaji', 'Margao', 'Vasco da Gama', 'Mapusa', 'Ponda', 'Mormugao', 'Quepem', 'Sanguem', 'Canacona', 'Bicholim', 'Pernem', 'Sattari', 'Tiswadi', 'Bardez', 'Salcete', 'Dharbandora'] },
        { name: 'Gujarat', districts: ['Ahmedabad', 'Surat', 'Vadodara', 'Rajkot', 'Bhavnagar', 'Jamnagar', 'Junagadh', 'Gandhinagar', 'Anand', 'Navsari', 'Mehsana', 'Bharuch', 'Surendranagar', 'Gandhidham', 'Nadiad', 'Morbi', 'Palanpur', 'Veraval', 'Godhra'] },
        { name: 'Haryana', districts: ['Chandigarh', 'Faridabad', 'Gurgaon', 'Panipat', 'Karnal', 'Hisar', 'Rohtak', 'Ambala', 'Sonipat', 'Yamunanagar', 'Kaithal', 'Kurukshetra', 'Rewari', 'Jind', 'Sirsa', 'Bhiwani', 'Palwal', 'Fatehabad', 'Mahendragarh', 'Nuh'] },
        { name: 'Himachal Pradesh', districts: ['Shimla', 'Mandi', 'Solan', 'Dharamshala', 'Kullu', 'Chamba', 'Una', 'Bilaspur', 'Hamirpur', 'Kangra', 'Kinnaur', 'Lahaul and Spiti', 'Sirmaur', 'Palampur', 'Nahan', 'Rampur', 'Sundernagar', 'Paonta Sahib', 'Nalagarh', 'Sujanpur'] },
        { name: 'Jharkhand', districts: ['Ranchi', 'Jamshedpur', 'Dhanbad', 'Bokaro', 'Deoghar', 'Hazaribagh', 'Giridih', 'Palamu', 'Chatra', 'Koderma', 'Garhwa', 'Simdega', 'Lohardaga', 'Gumla', 'Pakur', 'Sahebganj', 'Godda', 'Dumka', 'Jamtara', 'Saraikela'] },
        { name: 'Karnataka', districts: ['Bangalore', 'Mysore', 'Hubli', 'Mangalore', 'Belgaum', 'Gulbarga', 'Davanagere', 'Bellary', 'Bijapur', 'Shimoga', 'Tumkur', 'Raichur', 'Bidar', 'Hospet', 'Hassan', 'Mandya', 'Chitradurga', 'Kolar', 'Chikmagalur', 'Udupi'] },
        { name: 'Kerala', districts: ['Thiruvananthapuram', 'Kochi', 'Kozhikode', 'Thrissur', 'Kannur', 'Kollam', 'Alappuzha', 'Palakkad', 'Malappuram', 'Ernakulam', 'Idukki', 'Kottayam', 'Wayanad', 'Kasargod', 'Pathanamthitta', 'Calicut'] },
        { name: 'Madhya Pradesh', districts: ['Bhopal', 'Indore', 'Gwalior', 'Jabalpur', 'Ujjain', 'Sagar', 'Dewas', 'Satna', 'Ratlam', 'Rewa', 'Murwara', 'Singrauli', 'Burhanpur', 'Khandwa', 'Mandsaur', 'Neemuch', 'Chhindwara', 'Hoshangabad', 'Itarsi', 'Sehore'] },
        { name: 'Maharashtra', districts: ['Mumbai', 'Pune', 'Nagpur', 'Nashik', 'Aurangabad', 'Solapur', 'Amravati', 'Kolhapur', 'Sangli', 'Malegaon', 'Jalgaon', 'Akola', 'Latur', 'Ahmadnagar', 'Dhule', 'Chandrapur', 'Parbhani', 'Ichalkaranji', 'Jalna'] },
        { name: 'Manipur', districts: ['Imphal', 'Thoubal', 'Bishnupur', 'Churachandpur', 'Senapati', 'Tamenglong', 'Chandel', 'Ukhrul', 'Kangpokpi', 'Jiribam', 'Kakching', 'Tengnoupal', 'Kamjong', 'Noney', 'Pherzawl', 'Imphal East', 'Imphal West'] },
        { name: 'Meghalaya', districts: ['Shillong', 'Tura', 'Jowai', 'Nongstoin', 'Williamnagar', 'Baghmara', 'Resubelpara', 'Mairang', 'Mawkyrwat', 'Ampati', 'Khliehriat', 'Mawphlang', 'Sohra', 'Nongpoh', 'Mawryngkneng', 'Pynursla', 'Mawshynrut', 'Ranikor'] },
        { name: 'Mizoram', districts: ['Aizawl', 'Lunglei', 'Saiha', 'Champhai', 'Kolasib', 'Serchhip', 'Mamit', 'Lawngtlai', 'Saitual', 'Hnahthial', 'Khawzawl', 'Darlawn', 'Tlabung', 'Vairengte', 'Bilkhawthlir', 'West Phaileng', 'Ngopa', 'Reiek', 'Thenzawl'] },
        { name: 'Nagaland', districts: ['Kohima', 'Dimapur', 'Mokokchung', 'Tuensang', 'Wokha', 'Zunheboto', 'Phek', 'Mon', 'Longleng', 'Kiphire', 'Peren', 'Noklak', 'Shamator', 'Tseminyu', 'Chumukedima', 'Niuland', 'Medziphema', 'Jalukie', 'Pfutsero', 'Meluri'] },
        { name: 'Odisha', districts: ['Bhubaneswar', 'Cuttack', 'Rourkela', 'Berhampur', 'Sambalpur', 'Puri', 'Balasore', 'Bhadrak', 'Baripada', 'Jharsuguda', 'Bargarh', 'Sundargarh', 'Kendujhar', 'Mayurbhanj', 'Baleswar', 'Jajpur', 'Dhenkanal', 'Angul', 'Deogarh', 'Kandhamal'] },
        { name: 'Punjab', districts: ['Chandigarh', 'Ludhiana', 'Amritsar', 'Jalandhar', 'Patiala', 'Bathinda', 'Mohali', 'Moga', 'Firozpur', 'Batala', 'Abohar', 'Malerkotla', 'Khanna', 'Phagwara', 'Muktsar', 'Barnala', 'Rajpura', 'Fazilka', 'Kapurthala', 'Sunam'] },
        { name: 'Rajasthan', districts: ['Jaipur', 'Jodhpur', 'Udaipur', 'Kota', 'Ajmer', 'Bikaner', 'Bharatpur', 'Alwar', 'Bhilwara', 'Ganganagar', 'Sikar', 'Pali', 'Tonk', 'Sawai Madhopur', 'Churu', 'Jhunjhunu', 'Nagaur', 'Banswara', 'Dungarpur', 'Pratapgarh'] },
        { name: 'Sikkim', districts: ['Gangtok', 'Namchi', 'Mangan', 'Gyalshing', 'Soreng', 'Ravongla', 'Jorethang', 'Singtam', 'Rangpo', 'Pakyong', 'Rongli', 'Dikchu', 'Chungthang', 'Lachen', 'Lachung', 'Yuksom', 'Pelling', 'Ravangla'] },
        { name: 'Tamil Nadu', districts: ['Chennai', 'Coimbatore', 'Madurai', 'Tiruchirappalli', 'Salem', 'Tirunelveli', 'Tiruppur', 'Erode', 'Vellore', 'Thoothukkudi', 'Dindigul', 'Thanjavur', 'Ranipet', 'Sivaganga', 'Karur', 'Hosur', 'Nagercoil', 'Kanchipuram', 'Cuddalore', 'Kumbakonam'] },
        { name: 'Telangana', districts: ['Hyderabad', 'Warangal', 'Nizamabad', 'Khammam', 'Karimnagar', 'Ramagundam', 'Mahbubnagar', 'Nalgonda', 'Adilabad', 'Suryapet', 'Miryalaguda', 'Siddipet', 'Nirmal', 'Jagtial', 'Mancherial', 'Kamareddy', 'Sangareddy', 'Medak', 'Vikarabad', 'Wanaparthy'] },
        { name: 'Tripura', districts: ['Agartala', 'Dharmanagar', 'Udaipur', 'Kailashahar', 'Belonia', 'Khowai', 'Teliamura', 'Ambassa', 'Kamalpur', 'Sabroom', 'Sonamura', 'Melaghar', 'Bishalgarh', 'Jirania', 'Dukli', 'Hezamara'] },
        { name: 'Uttar Pradesh', districts: ['Lucknow', 'Kanpur', 'Agra', 'Varanasi', 'Meerut', 'Allahabad', 'Bareilly', 'Ghaziabad', 'Moradabad', 'Aligarh', 'Saharanpur', 'Gorakhpur', 'Firozabad', 'Jhansi', 'Muzaffarnagar', 'Mathura', 'Shahjahanpur', 'Rampur', 'Modinagar', 'Hapur'] },
        { name: 'Uttarakhand', districts: ['Dehradun', 'Haridwar', 'Rishikesh', 'Nainital', 'Mussoorie', 'Haldwani', 'Roorkee', 'Kashipur', 'Rudrapur', 'Kotdwar', 'Pithoragarh', 'Almora', 'Ranikhet', 'Champawat', 'Bageshwar', 'Chamoli', 'Pauri', 'Tehri', 'Uttarkashi', 'Rudraprayag'] },
        { name: 'West Bengal', districts: ['Kolkata', 'Howrah', 'Durgapur', 'Asansol', 'Siliguri', 'Malda', 'Baharampur', 'Habra', 'Kharagpur', 'Shantipur', 'Dankuni', 'Dhulian', 'Ranaghat', 'Haldia', 'Raiganj', 'Krishnanagar', 'Nabadwip', 'Medinipur', 'Jalpaiguri', 'Balurghat'] }
    ];

    const defaultValidationFields = [
        'name', 'email', 'phone', 'address',
        'age', 'qualification', 'experience',
        'documents', 'id_proof', 'photo',
        'parent_name', 'parent_phone', 'parent_email', 'family_income'
    ];

    const updateDesignationsBasedOnScope = (regions: string[], states: string[]) => {
        let newDesignations = [...formData.targetDesignations];
        if (regions.length > 0) {
            newDesignations = ['team_executive', 'team_leader', 'district_head', 'state_head', 'regional_head'];
        } else if (states.length > 0) {
            newDesignations = ['team_executive', 'team_leader', 'district_head', 'state_head'];
        }
        setFormData(prev => ({ ...prev, targetDesignations: newDesignations }));
    };

    const handleRegionChange = (region: string, isChecked: boolean) => {
        let newRegions = [...formData.regions];
        let newStates = [...formData.states];
        let newDistricts = [...formData.districts];

        if (isChecked) {
            newRegions.push(region);
            const regionStates = regionStatesMap[region as keyof typeof regionStatesMap] || [];
            regionStates.forEach(state => {
                if (!newStates.includes(state)) newStates.push(state);
            });
            regionStates.forEach(stateName => {
                const state = stateData.find(s => s.name === stateName);
                if (state) {
                    state.districts.forEach(district => {
                        if (!newDistricts.includes(district)) newDistricts.push(district);
                    });
                }
            });
        } else {
            newRegions = newRegions.filter(r => r !== region);
            const regionStates = regionStatesMap[region as keyof typeof regionStatesMap] || [];
            newStates = newStates.filter(state => !regionStates.includes(state));
            regionStates.forEach(stateName => {
                const state = stateData.find(s => s.name === stateName);
                if (state) {
                    newDistricts = newDistricts.filter(district => !state.districts.includes(district));
                }
            });
        }

        setFormData(prev => ({
            ...prev,
            regions: newRegions,
            states: newStates,
            districts: newDistricts
        }));
        updateDesignationsBasedOnScope(newRegions, newStates);
    };

    const handleStateChange = (stateName: string, isChecked: boolean) => {
        let newStates = [...formData.states];
        let newDistricts = [...formData.districts];

        if (isChecked) {
            newStates.push(stateName);
            const state = stateData.find(s => s.name === stateName);
            if (state) {
                state.districts.forEach(district => {
                    if (!newDistricts.includes(district)) newDistricts.push(district);
                });
            }
        } else {
            newStates = newStates.filter(s => s !== stateName);
            const state = stateData.find(s => s.name === stateName);
            if (state) {
                newDistricts = newDistricts.filter(d => !state.districts.includes(d));
            }
        }

        setFormData(prev => ({
            ...prev,
            states: newStates,
            districts: newDistricts
        }));
        updateDesignationsBasedOnScope(formData.regions, newStates);
    };

    const handleValidationFieldChange = (field: string) => {
        setFormData(prev => ({
            ...prev,
            validationRules: {
                requiredFields: prev.validationRules.requiredFields.includes(field)
                    ? prev.validationRules.requiredFields.filter(f => f !== field)
                    : [...prev.validationRules.requiredFields, field]
            }
        }));
    };

    const addValidationField = () => {
        const fieldName = prompt('Enter field name:');
        if (fieldName && fieldName.trim()) {
            handleValidationFieldChange(fieldName.trim());
        }
    };

    // Budget Scenario Management
    const addBudgetScenario = () => {
        const scenario = {
            id: Date.now().toString(),
            name: newScenario.name,
            description: newScenario.description,
            range: newScenario.range,
            budgetDistribution: newScenario.budgetDistribution,
            isActive: true
        };

        setFormData(prev => ({
            ...prev,
            budgetScenarios: [...prev.budgetScenarios, scenario],
            currentScenario: prev.budgetScenarios.length === 0 ? scenario.id : prev.currentScenario
        }));

        setNewScenario({
            name: '',
            description: '',
            range: { min: 1, max: 500 },
            budgetDistribution: {
                teamExecutive: 0,
                teamLeader: 0,
                districtHead: 0,
                stateHead: 0,
                regionalHead: 0,
                admin: 0,
            },
            isActive: false
        });
        setShowBudgetScenarioModal(false);
    };

    const updateBudgetScenario = (scenarioId: string, updatedScenario: any) => {
        setFormData(prev => ({
            ...prev,
            budgetScenarios: prev.budgetScenarios.map(scenario =>
                scenario.id === scenarioId ? updatedScenario : scenario
            )
        }));
        setEditingScenario(null);
        setShowBudgetScenarioModal(false);
    };

    const deleteBudgetScenario = (scenarioId: string) => {
        setFormData(prev => ({
            ...prev,
            budgetScenarios: prev.budgetScenarios.filter(scenario => scenario.id !== scenarioId)
        }));
    };

    const toggleBudgetScenario = (scenarioId: string) => {
        setFormData(prev => ({
            ...prev,
            budgetScenarios: prev.budgetScenarios.map(scenario => ({
                ...scenario,
                isActive: scenario.id === scenarioId ? !scenario.isActive : scenario.isActive
            }))
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const cleanBudgetScenarios = formData.budgetScenarios.filter(scenario => {
                return scenario.name &&
                    scenario.name.trim() !== '' &&
                    scenario.range &&
                    typeof scenario.range.min === 'number' &&
                    typeof scenario.range.max === 'number' &&
                    scenario.range.min < scenario.range.max;
            });

            const taskData = {
                title: formData.title,
                description: formData.description,
                taskUrl: formData.taskUrl,
                totalBudget: parseFloat(formData.totalBudget),
                budgetScenarios: cleanBudgetScenarios,
                currentScenario: cleanBudgetScenarios.length > 0 ? formData.currentScenario : null,
                regions: formData.regions,
                states: formData.states,
                districts: formData.districts,
                targetDesignations: formData.targetDesignations,
                requiredSkills: formData.requiredSkills,
                startDate: formData.startDate,
                endDate: formData.endDate,
                validationRules: {
                    requiredFields: formData.validationRules.requiredFields || []
                },
                ...(user?.role === 'admin' && selectedCompany && { company: selectedCompany })
            };

            await dispatch(updateTask({ taskId: id!, taskData })).unwrap();

            dispatch(showToast({
                message: 'Task updated successfully!',
                type: 'success'
            }));

            navigate('/admin/tasks');
        } catch (error: any) {
            console.error('Task update error:', error);
            dispatch(showToast({
                message: error.message || 'Failed to update task',
                type: 'error'
            }));
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-400">Loading task data...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={() => navigate('/admin/tasks')}
                        className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4 transition-colors"
                    >
                        <ArrowLeftIcon className="h-5 w-5 mr-2" />
                        Back to Tasks
                    </button>
                    <div className="flex items-center">
                        <ClipboardDocumentListIcon className="h-8 w-8 text-blue-600 mr-3" />
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Edit Task</h1>
                    </div>
                </div>

                {/* Form */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700">
                    <form onSubmit={handleSubmit} className="p-8 space-y-8">
                        {/* Basic Information */}
                        <div>
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
                                <BuildingOfficeIcon className="h-6 w-6 text-blue-600 mr-2" />
                                Basic Information
                            </h2>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <div className="lg:col-span-2">
                                    <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Task Title *
                                    </label>
                                    <input
                                        type="text"
                                        id="title"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                        placeholder="Enter task title"
                                    />
                                </div>

                                {/* Company Selection - Only for Admin */}
                                {user?.role === 'admin' && (
                                    <div className="lg:col-span-2">
                                        <label htmlFor="company" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Company *
                                        </label>
                                        <div className="relative">
                                            <BuildingOfficeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                            <select
                                                id="company"
                                                value={selectedCompany}
                                                onChange={(e) => setSelectedCompany(e.target.value)}
                                                required
                                                className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 appearance-none"
                                            >
                                                <option value="">Select a company</option>
                                                {companies.map((company) => (
                                                    <option key={company._id} value={company._id}>
                                                        {company.companyName}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                )}

                                <div className="lg:col-span-2">
                                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Description *
                                    </label>
                                    <textarea
                                        id="description"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleChange}
                                        rows={4}
                                        required
                                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                        placeholder="Describe the task in detail..."
                                    />
                                </div>

                                <div className="lg:col-span-2">
                                    <label htmlFor="taskUrl" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Task URL *
                                    </label>
                                    <input
                                        type="url"
                                        id="taskUrl"
                                        name="taskUrl"
                                        value={formData.taskUrl}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                        placeholder="https://example.com/task"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Required Skills */}
                        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-8">
                            <div className="flex items-center mb-6">
                                <UserGroupIcon className="h-6 w-6 text-green-600 dark:text-green-400 mr-3" />
                                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                                    Required Skills
                                </h2>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Select Required Skills *
                                </label>
                                <SkillsDropdown
                                    selectedSkills={formData.requiredSkills}
                                    onSkillsChange={(skills) => handleArrayChange('requiredSkills', skills)}
                                    placeholder="Search and select skills"
                                    maxSkills={10}
                                    showSearch={true}
                                    className="w-full"
                                />
                            </div>
                        </div>

                        {/* Budget Information */}
                        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-8">
                            <div className="flex items-center mb-6">
                                <CurrencyDollarIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400 mr-3" />
                                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                                    Budget Distribution
                                </h2>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <div>
                                    <label htmlFor="totalBudget" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Total Budget Per Task(₹) *
                                    </label>
                                    <input
                                        type="number"
                                        id="totalBudget"
                                        name="totalBudget"
                                        value={formData.totalBudget}
                                        onChange={handleChange}
                                        required
                                        min="0"
                                        step="0.01"
                                        placeholder="Enter total budget amount"
                                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                    />
                                </div>

                                <div className="lg:col-span-2">
                                    <div className="flex items-center justify-between mb-4">
                                        <div>
                                            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                                                Budget Scenarios
                                            </h3>
                                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                                Create scenarios with different budget distributions.
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setShowBudgetScenarioModal(true)}
                                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium flex items-center gap-2"
                                        >
                                            <PlusIcon className="h-4 w-4" />
                                            Add Scenario
                                        </button>
                                    </div>

                                    {formData.budgetScenarios.length > 0 && (
                                        <div className="space-y-4">
                                            {formData.budgetScenarios.map((scenario) => (
                                                <div key={scenario.id} className={`p-4 rounded-xl border ${scenario.isActive
                                                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                                                    : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600'
                                                    }`}>
                                                    <div className="flex items-center justify-between mb-3">
                                                        <div>
                                                            <div className="flex items-center gap-2">
                                                                <h5 className="font-semibold text-gray-900 dark:text-white">{scenario.name}</h5>
                                                                {scenario.isActive && (
                                                                    <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded-full">
                                                                        ACTIVE
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <p className="text-sm text-gray-600 dark:text-gray-400">{scenario.description}</p>
                                                            <div className="mt-1">
                                                                <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                                                                    Range: {scenario.range.min} - {scenario.range.max} tasks
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center space-x-2">
                                                            <button
                                                                type="button"
                                                                onClick={() => toggleBudgetScenario(scenario.id)}
                                                                className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${scenario.isActive
                                                                    ? 'bg-green-600 text-white hover:bg-green-700'
                                                                    : 'bg-gray-400 text-white hover:bg-gray-500'
                                                                    }`}
                                                            >
                                                                {scenario.isActive ? 'Active' : 'Inactive'}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    setEditingScenario(scenario);
                                                                    setNewScenario(scenario);
                                                                    setShowBudgetScenarioModal(true);
                                                                }}
                                                                className="p-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                                                            >
                                                                <PencilIcon className="h-4 w-4" />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => deleteBudgetScenario(scenario.id)}
                                                                className="p-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                                                            >
                                                                <TrashIcon className="h-4 w-4" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Geographic Scope */}
                        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-8">
                            <div className="flex items-center mb-6">
                                <MapPinIcon className="h-6 w-6 text-purple-600 dark:text-purple-400 mr-3" />
                                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                                    Geographic Scope
                                </h2>
                            </div>

                            <div className="space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Target Regions
                                    </label>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                                        {regions.map((region) => (
                                            <label key={region} className="flex items-center space-x-2 p-3 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.regions.includes(region)}
                                                    onChange={(e) => handleRegionChange(region, e.target.checked)}
                                                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                                />
                                                <span className="capitalize text-gray-700 dark:text-gray-300">{region}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {/* States Selection - only show if Regions are selected or States are populated */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Target States
                                    </label>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 max-h-60 overflow-y-auto p-4 border border-gray-200 dark:border-gray-700 rounded-xl">
                                        {Object.values(regionStatesMap).flat().sort().map((state) => (
                                            <label key={state} className="flex items-center space-x-2">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.states.includes(state)}
                                                    onChange={(e) => handleStateChange(state, e.target.checked)}
                                                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                                />
                                                <span className="text-sm text-gray-700 dark:text-gray-300">{state}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Target Designations
                                    </label>
                                    <div className="flex flex-wrap gap-2">
                                        {designations.map((designation) => (
                                            <div
                                                key={designation}
                                                className={`px-3 py-1 rounded-full text-sm font-medium border ${formData.targetDesignations.includes(designation)
                                                    ? 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900 dark:text-blue-200 dark:border-blue-800'
                                                    : 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600'
                                                    }`}
                                            >
                                                {designation.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Timeline & Status */}
                        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-8">
                            <div className="flex items-center mb-6">
                                <CalendarIcon className="h-6 w-6 text-orange-600 dark:text-orange-400 mr-3" />
                                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                                    Timeline
                                </h2>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Start Date *
                                    </label>
                                    <input
                                        type="date"
                                        name="startDate"
                                        value={formData.startDate}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        End Date *
                                    </label>
                                    <input
                                        type="date"
                                        name="endDate"
                                        value={formData.endDate}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Data Collection Fields */}
                        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-8">
                            <div className="flex items-center mb-6">
                                <CogIcon className="h-6 w-6 text-gray-600 dark:text-gray-400 mr-3" />
                                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                                    Data Collection Fields
                                </h2>
                            </div>

                            <div className="space-y-4">
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                                    {defaultValidationFields.map((field) => (
                                        <label key={field} className="flex items-center space-x-2">
                                            <input
                                                type="checkbox"
                                                checked={formData.validationRules.requiredFields.includes(field)}
                                                onChange={() => handleValidationFieldChange(field)}
                                                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                            />
                                            <span className="text-sm text-gray-700 dark:text-gray-300 capitalize">
                                                {field.replace(/_/g, ' ')}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                                <button
                                    type="button"
                                    onClick={addValidationField}
                                    className="mt-4 px-4 py-2 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 font-medium flex items-center"
                                >
                                    <PlusIcon className="h-4 w-4 mr-1" />
                                    Add Custom Field
                                </button>
                            </div>
                        </div>

                        {/* Submit Actions */}
                        <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                            <button
                                type="button"
                                onClick={() => navigate('/admin/tasks')}
                                className="px-6 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="px-6 py-3 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-xl transition-colors flex items-center"
                            >
                                {isSubmitting ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        Updating...
                                    </>
                                ) : (
                                    <>
                                        <CheckCircleIcon className="h-4 w-4 mr-2" />
                                        Update Task
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Budget Scenario Modal */}
            {showBudgetScenarioModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                            <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                        </div>
                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
                            <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                <div className="sm:flex sm:items-start">
                                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                                        <div className="flex justify-between items-center mb-4">
                                            <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                                                {editingScenario ? 'Edit Budget Scenario' : 'Add Budget Scenario'}
                                            </h3>
                                            <button
                                                onClick={() => setShowBudgetScenarioModal(false)}
                                                className="text-gray-400 hover:text-gray-500"
                                            >
                                                <XMarkIcon className="h-6 w-6" />
                                            </button>
                                        </div>

                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
                                                <input
                                                    type="text"
                                                    value={newScenario.name}
                                                    onChange={(e) => setNewScenario(prev => ({ ...prev, name: e.target.value }))}
                                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                                    placeholder="Scenario Name (e.g., Level 1)"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
                                                <input
                                                    type="text"
                                                    value={newScenario.description}
                                                    onChange={(e) => setNewScenario(prev => ({ ...prev, description: e.target.value }))}
                                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                                    placeholder="Description"
                                                />
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Min Tasks</label>
                                                    <input
                                                        type="number"
                                                        value={newScenario.range.min}
                                                        onChange={(e) => setNewScenario(prev => ({ ...prev, range: { ...prev.range, min: parseInt(e.target.value) } }))}
                                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Max Tasks</label>
                                                    <input
                                                        type="number"
                                                        value={newScenario.range.max}
                                                        onChange={(e) => setNewScenario(prev => ({ ...prev, range: { ...prev.range, max: parseInt(e.target.value) } }))}
                                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                                    />
                                                </div>
                                            </div>

                                            <div>
                                                <h4 className="text-md font-medium text-gray-900 dark:text-white mb-2">Budget Distribution (₹)</h4>
                                                <div className="grid grid-cols-2 gap-4">
                                                    {Object.entries(newScenario.budgetDistribution).map(([key, value]) => (
                                                        <div key={key}>
                                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">
                                                                {key.replace(/([A-Z])/g, ' $1').trim()}
                                                            </label>
                                                            <input
                                                                type="number"
                                                                value={value}
                                                                onChange={(e) => setNewScenario(prev => ({
                                                                    ...prev,
                                                                    budgetDistribution: {
                                                                        ...prev.budgetDistribution,
                                                                        [key]: parseFloat(e.target.value)
                                                                    }
                                                                }))}
                                                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                <button
                                    type="button"
                                    onClick={editingScenario ? () => updateBudgetScenario(editingScenario.id, newScenario) : addBudgetScenario}
                                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                                >
                                    {editingScenario ? 'Update' : 'Add'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowBudgetScenarioModal(false)}
                                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EditTask;
